import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { fetchWithLocalCache } from "../lib/cache";
import {
  ResponsiveContainer,
  CartesianGrid,
  Tooltip,
  Legend,
  XAxis,
  YAxis,
  Bar,
  Line,
  ComposedChart,
  LabelList,
} from "recharts";

const FUNDAMENTAL_TRENDS_CANDIDATE_URLS = [
  "https://raw.githubusercontent.com/aport550/majorah-data/main/public/data/fundamental_trends.json",
  "https://cdn.jsdelivr.net/gh/aport550/majorah-data@main/public/data/fundamental_trends.json",
];

const BALANCE_SHEET_DILUTION_CANDIDATE_URLS = [
  "https://raw.githubusercontent.com/aport550/majorah-data/main/public/data/stock_balance_sheet_dilution.json",
  "https://cdn.jsdelivr.net/gh/aport550/majorah-data@main/public/data/stock_balance_sheet_dilution.json",
];

function isFiniteNumber(v) {
  return typeof v === "number" && Number.isFinite(v);
}

function normalizeTickerSymbol(v) {
  return String(v || "").trim().toUpperCase();
}

function normalizeKey(k) {
  return String(k || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ")
    .replace(/[^\w %/.\-<=]/g, "");
}

function buildKeyIndex(obj) {
  const idx = new Map();
  if (!obj || typeof obj !== "object") return idx;
  for (const k of Object.keys(obj)) idx.set(normalizeKey(k), k);
  return idx;
}

function getAny(row, keyIndex, candidates) {
  for (const cand of candidates) {
    const real = keyIndex.get(normalizeKey(cand));
    if (real != null) {
      const v = row?.[real];
      if (v !== null && v !== undefined && String(v).trim() !== "") return v;
    }
  }
  return null;
}

function parseMaybeNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const cleaned = String(value).replace(/[$,% ,]/g, "").trim();
  const num = Number(cleaned);
  return Number.isFinite(num) ? num : null;
}

function formatPrice(v) {
  if (!isFiniteNumber(v)) return "—";
  return `$${v.toFixed(2)}`;
}

function formatPlainNumber(v, decimals = 2) {
  if (!isFiniteNumber(v)) return "—";
  return Number(v).toFixed(decimals);
}

function formatScore(v) {
  if (!isFiniteNumber(v)) return "—";
  return Number(v).toFixed(1);
}

function formatPercentMaybeDecimal(v, decimals = 2) {
  if (!isFiniteNumber(v)) return "—";
  const pct = Math.abs(v) <= 1.5 ? v * 100 : v;
  return `${pct.toFixed(decimals)}%`;
}

function formatCurrencyCompact(v) {
  if (!isFiniteNumber(v)) return "—";
  const abs = Math.abs(v);
  const prefix = v < 0 ? "-$" : "$";
  if (abs >= 1e12) return `${prefix}${(abs / 1e12).toFixed(2)}T`;
  if (abs >= 1e9) return `${prefix}${(abs / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `${prefix}${(abs / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `${prefix}${(abs / 1e3).toFixed(2)}K`;
  return `${prefix}${abs.toFixed(0)}`;
}

function fmtQuarterDate(raw) {
  const s = String(raw || "").trim();
  if (!s) return "";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString(undefined, {
    year: "2-digit",
    month: "short",
  });
}

function shiftQuarterLabel(rawDate, quarterOffset) {
  const s = String(rawDate || "").trim();
  if (!s) return "";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "";

  const shifted = new Date(d);
  shifted.setMonth(shifted.getMonth() - quarterOffset * 3);

  return shifted.toLocaleDateString(undefined, {
    year: "2-digit",
    month: "short",
  });
}

function scorePillStyle(score, inverse = false) {
  if (!isFiniteNumber(score)) return { background: "#f3f4f6", color: "#6b7280" };
  const s = Math.max(0, Math.min(100, Number(score)));
  const good = inverse ? 100 - s : s;
  if (good >= 75) return { background: "#ecfdf5", color: "#166534" };
  if (good >= 55) return { background: "#fefce8", color: "#854d0e" };
  return { background: "#fef2f2", color: "#991b1b" };
}

function getTrendDirectionStyle(v) {
  if (!isFiniteNumber(v)) return { color: "#6b7280" };
  if (v > 0) return { color: "#15803d", fontWeight: 700 };
  if (v < 0) return { color: "#b91c1c", fontWeight: 700 };
  return { color: "#374151", fontWeight: 600 };
}

async function fetchJsonFromCandidates(urls) {
  let lastError = null;
  for (const url of urls) {
    try {
      const res = await fetch(url, { cache: "no-store" });
      if (!res.ok) {
        lastError = new Error(`HTTP ${res.status} from ${url}`);
        continue;
      }
      const text = await res.text();
      const trimmed = text.trim();
      if (!trimmed) {
        lastError = new Error(`Empty response from ${url}`);
        continue;
      }
      if (
        trimmed.startsWith("<!DOCTYPE") ||
        trimmed.startsWith("<html") ||
        trimmed.startsWith("<")
      ) {
        lastError = new Error(`Expected JSON but received HTML from ${url}`);
        continue;
      }
      return JSON.parse(text);
    } catch (err) {
      lastError = err;
    }
  }
  throw lastError || new Error("Unable to load JSON.");
}

function FundamentalTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0]?.payload || {};
  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: 10,
        padding: 10,
        boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
        fontSize: 12,
      }}
    >
      <div style={{ fontWeight: 700, marginBottom: 6 }}>{label}</div>
      <div>Revenue: {formatCurrencyCompact(p.revenue)}</div>
      <div>Operating Margin: {formatPercentMaybeDecimal(p.operatingMargin, 1)}</div>
      <div>Gross Margin: {formatPercentMaybeDecimal(p.grossMargin, 1)}</div>
    </div>
  );
}

function StatCard({ label, value, sublabel, pillStyle }) {
  return (
    <div
      style={{
        border: "1px solid #f3f4f6",
        borderRadius: 12,
        padding: 12,
        background: "#fafafa",
      }}
    >
      <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 6 }}>{label}</div>
      {pillStyle ? (
        <div
          style={{
            display: "inline-block",
            padding: "6px 10px",
            borderRadius: 9999,
            fontWeight: 800,
            fontSize: 18,
            ...pillStyle,
          }}
        >
          {value}
        </div>
      ) : (
        <div style={{ fontWeight: 800, fontSize: 18, color: "#111827" }}>{value}</div>
      )}
      {sublabel ? (
        <div style={{ marginTop: 6, fontSize: 12, color: "#6b7280" }}>{sublabel}</div>
      ) : null}
    </div>
  );
}

export default function StockFundamentalsDetail() {
  const params = useParams();
  const sym = String(params.symbol || params.ticker || params.id || "")
    .trim()
    .toUpperCase();
  const [searchParams] = useSearchParams();
  const exParam = String(searchParams.get("ex") || "").trim().toUpperCase();

  const [headerRow, setHeaderRow] = useState(null);
  const [headerError, setHeaderError] = useState("");

  const [fundamentalRow, setFundamentalRow] = useState(null);
  const [fundamentalError, setFundamentalError] = useState("");
  const [loadingFundamentals, setLoadingFundamentals] = useState(true);

  const [balanceRow, setBalanceRow] = useState(null);
  const [balanceError, setBalanceError] = useState("");
  const [loadingBalance, setLoadingBalance] = useState(true);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        setHeaderError("");
        setHeaderRow(null);

        const base = process.env.REACT_APP_SHEETS_API_URL;
        const apiKey = process.env.REACT_APP_API_KEY;
        if (!base || !apiKey || !sym) return;

        const detailUrlTicker = `${base}?type=stocks&view=detail&ticker=${encodeURIComponent(
          sym
        )}&key=${encodeURIComponent(apiKey)}`;
        const detailUrlSymbol = `${base}?type=stocks&view=detail&symbol=${encodeURIComponent(
          sym
        )}&key=${encodeURIComponent(apiKey)}`;

        async function fetchRecord(url, cacheKey) {
          const res = await fetchWithLocalCache(url, cacheKey, 5 * 60 * 1000);
          const payload = res?.data ?? res;
          if (payload?.error) throw new Error(payload.error);
          let record = payload?.row ?? payload;
          if (Array.isArray(record)) record = record[0] ?? null;
          return record && typeof record === "object" ? record : null;
        }

        function getRecordSymbol(record) {
          if (!record || typeof record !== "object") return "";
          const idx = buildKeyIndex(record);
          const raw = getAny(record, idx, ["Symbol", "Ticker"]);
          return String(raw || "").trim().toUpperCase();
        }

        let record = await fetchRecord(detailUrlTicker, `stock_detail_${sym}_ticker`);
        if (!record || getRecordSymbol(record) !== sym) {
          record = await fetchRecord(detailUrlSymbol, `stock_detail_${sym}_symbol`);
        }

        if (!cancelled) setHeaderRow(record || null);
      } catch (err) {
        if (!cancelled)
          setHeaderError(err?.message || "Unable to load stock header.");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [sym]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoadingFundamentals(true);
        setFundamentalError("");
        setFundamentalRow(null);

        const json = await fetchJsonFromCandidates(FUNDAMENTAL_TRENDS_CANDIDATE_URLS);

        let match = null;
        if (json && typeof json === "object" && json.stocks && typeof json.stocks === "object") {
          match = json.stocks?.[sym] || json.stocks?.[normalizeTickerSymbol(sym)] || null;
        }

        if (!match) {
          const rows = Array.isArray(json)
            ? json
            : Array.isArray(json?.rows)
            ? json.rows
            : Array.isArray(json?.data)
            ? json.data
            : [];
          match =
            rows.find((row) => normalizeTickerSymbol(row?.ticker || row?.symbol) === sym) ||
            null;
        }

        if (!cancelled) setFundamentalRow(match);
      } catch (err) {
        if (!cancelled)
          setFundamentalError(err?.message || "Failed to load fundamental trends.");
      } finally {
        if (!cancelled) setLoadingFundamentals(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sym]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoadingBalance(true);
        setBalanceError("");
        setBalanceRow(null);

        const json = await fetchJsonFromCandidates(BALANCE_SHEET_DILUTION_CANDIDATE_URLS);
        const rows = Array.isArray(json)
          ? json
          : Array.isArray(json?.rows)
          ? json.rows
          : Array.isArray(json?.data)
          ? json.data
          : [];
        const match =
          rows.find((row) => normalizeTickerSymbol(row?.ticker || row?.symbol) === sym) ||
          null;
        if (!cancelled) setBalanceRow(match);
      } catch (err) {
        if (!cancelled)
          setBalanceError(
            err?.message || "Failed to load balance sheet / dilution metrics."
          );
      } finally {
        if (!cancelled) setLoadingBalance(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sym]);

  const headerKeyIndex = useMemo(() => buildKeyIndex(headerRow), [headerRow]);
  const fundamentalKeyIndex = useMemo(() => buildKeyIndex(fundamentalRow), [fundamentalRow]);

  const revenueObj = useMemo(() => {
    if (!fundamentalRow || typeof fundamentalRow !== "object") return null;
    return fundamentalRow.revenue && typeof fundamentalRow.revenue === "object"
      ? fundamentalRow.revenue
      : fundamentalRow;
  }, [fundamentalRow]);

  const operatingMarginObj = useMemo(() => {
    if (!fundamentalRow || typeof fundamentalRow !== "object") return null;
    return fundamentalRow.operatingMargin &&
      typeof fundamentalRow.operatingMargin === "object"
      ? fundamentalRow.operatingMargin
      : fundamentalRow;
  }, [fundamentalRow]);

  const grossMarginObj = useMemo(() => {
    if (!fundamentalRow || typeof fundamentalRow !== "object") return null;
    return fundamentalRow.grossMargin && typeof fundamentalRow.grossMargin === "object"
      ? fundamentalRow.grossMargin
      : fundamentalRow;
  }, [fundamentalRow]);

  const revenueKeyIndex = useMemo(() => buildKeyIndex(revenueObj), [revenueObj]);
  const operatingMarginKeyIndex = useMemo(
    () => buildKeyIndex(operatingMarginObj),
    [operatingMarginObj]
  );
  const grossMarginKeyIndex = useMemo(() => buildKeyIndex(grossMarginObj), [grossMarginObj]);

  const stockPrice = useMemo(() => {
    const raw = getAny(headerRow, headerKeyIndex, [
      "Price",
      "Last",
      "Last Price",
      "Current Price",
      "Close",
    ]);
    return parseMaybeNumber(raw);
  }, [headerRow, headerKeyIndex]);

  const headlineScores = useMemo(() => {
    return {
      buy: parseMaybeNumber(getAny(headerRow, headerKeyIndex, ["Buy Score"])),
      valuation: parseMaybeNumber(
        getAny(headerRow, headerKeyIndex, ["Valuation Rating", "Valuation Score"])
      ),
      growth: parseMaybeNumber(
        getAny(headerRow, headerKeyIndex, ["Growth Rating", "Growth Score"])
      ),
      profit: parseMaybeNumber(
        getAny(headerRow, headerKeyIndex, ["Profitability Rating", "Profitability Score"])
      ),
      defensiveness: parseMaybeNumber(
        getAny(headerRow, headerKeyIndex, ["Defensive Rating", "Defensiveness Score"])
      ),
      insider: parseMaybeNumber(
        getAny(headerRow, headerKeyIndex, ["Insider Rating", "Insider Score"])
      ),
      institutional: parseMaybeNumber(
        getAny(headerRow, headerKeyIndex, ["Institutional Rating", "Institutional Score"])
      ),
      balanceSheetSheet: parseMaybeNumber(
        getAny(headerRow, headerKeyIndex, ["Balance Sheet Rating", "Balance Sheet Score"])
      ),
      rsi: parseMaybeNumber(getAny(headerRow, headerKeyIndex, ["RSI"])),
    };
  }, [headerRow, headerKeyIndex]);

  const quarterChartRows = useMemo(() => {
    if (!fundamentalRow) return [];

    const latestDate = getAny(revenueObj, revenueKeyIndex, ["latestDate", "latest date"]);
    const latestPeriod = String(
      getAny(revenueObj, revenueKeyIndex, ["latestPeriod", "latest period"]) || ""
    ).trim();
    const latestYear = String(
      getAny(revenueObj, revenueKeyIndex, ["latestYear", "latest year"]) || ""
    ).trim();

    const revenueKeys = ["R3", "R2", "R1", "R0"];
    const opMarginKeys = ["O3", "O2", "O1", "O0"];
    const grossMarginKeys = ["G3", "G2", "G1", "G0"];

    return revenueKeys.map((rKey, idx) => {
      const quarterOffset = 3 - idx;
      const actualPeriodLabel = latestDate
        ? shiftQuarterLabel(latestDate, quarterOffset)
        : idx === 3 && (latestPeriod || latestYear)
        ? `${latestPeriod} ${latestYear}`.trim()
        : `T-${quarterOffset}`;

      return {
        quarter: actualPeriodLabel,
        shortLabel: actualPeriodLabel,
        revenue: parseMaybeNumber(getAny(revenueObj, revenueKeyIndex, [rKey, rKey.toLowerCase()])),
        operatingMargin: parseMaybeNumber(
          getAny(operatingMarginObj, operatingMarginKeyIndex, [
            opMarginKeys[idx],
            opMarginKeys[idx].toLowerCase(),
          ])
        ),
        grossMargin: parseMaybeNumber(
          getAny(grossMarginObj, grossMarginKeyIndex, [
            grossMarginKeys[idx],
            grossMarginKeys[idx].toLowerCase(),
          ])
        ),
      };
    });
  }, [
    fundamentalRow,
    revenueObj,
    revenueKeyIndex,
    operatingMarginObj,
    operatingMarginKeyIndex,
    grossMarginObj,
    grossMarginKeyIndex,
  ]);

  const trendMetrics = useMemo(() => {
    if (!fundamentalRow) return null;
    return {
      tamScore: parseMaybeNumber(getAny(fundamentalRow, fundamentalKeyIndex, ["tamScore", "tam score"])),
      moatScore: parseMaybeNumber(getAny(fundamentalRow, fundamentalKeyIndex, ["moatScore", "moat score"])),
      opTrendScore: parseMaybeNumber(
        getAny(fundamentalRow, fundamentalKeyIndex, [
          "operatingMarginTrendScore",
          "operating margin trend score",
        ])
      ),
      grossTrendScore: parseMaybeNumber(
        getAny(fundamentalRow, fundamentalKeyIndex, [
          "grossMarginTrendScore",
          "gross margin trend score",
        ])
      ),
      r01: parseMaybeNumber(getAny(revenueObj, revenueKeyIndex, ["r01"])),
      r12: parseMaybeNumber(getAny(revenueObj, revenueKeyIndex, ["r12"])),
      r03: parseMaybeNumber(getAny(revenueObj, revenueKeyIndex, ["r03"])),
      o01: parseMaybeNumber(getAny(operatingMarginObj, operatingMarginKeyIndex, ["o01"])),
      o12: parseMaybeNumber(getAny(operatingMarginObj, operatingMarginKeyIndex, ["o12"])),
      o03: parseMaybeNumber(getAny(operatingMarginObj, operatingMarginKeyIndex, ["o03"])),
      g01: parseMaybeNumber(getAny(grossMarginObj, grossMarginKeyIndex, ["g01"])),
      g12: parseMaybeNumber(getAny(grossMarginObj, grossMarginKeyIndex, ["g12"])),
      g03: parseMaybeNumber(getAny(grossMarginObj, grossMarginKeyIndex, ["g03"])),
      latestDate: getAny(revenueObj, revenueKeyIndex, ["latestDate", "latest date"]),
    };
  }, [
    fundamentalRow,
    fundamentalKeyIndex,
    revenueObj,
    revenueKeyIndex,
    operatingMarginObj,
    operatingMarginKeyIndex,
    grossMarginObj,
    grossMarginKeyIndex,
  ]);

  const netBuybackRate1Y = useMemo(() => {
    const shareGrowth1Y = parseMaybeNumber(balanceRow?.shareCountGrowth1Y);
    if (!isFiniteNumber(shareGrowth1Y)) return null;
    return -shareGrowth1Y;
  }, [balanceRow]);

  return (
    <div style={{ maxWidth: 1380, margin: "0 auto", padding: 16, fontFamily: "system-ui" }}>
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 16,
          flexWrap: "wrap",
          marginBottom: 16,
        }}
      >
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <h2 style={{ margin: 0, lineHeight: 1.1 }}>{sym || "Stock"}</h2>
            <div style={{ fontSize: 20, fontWeight: 700, color: "#111827" }}>
              {formatPrice(stockPrice)}
            </div>
          </div>
          {exParam ? (
            <div style={{ color: "#6b7280", fontSize: 13, marginTop: 4 }}>{exParam}</div>
          ) : null}
          {headerError ? (
            <div style={{ color: "#9ca3af", fontSize: 12, marginTop: 4 }}>{headerError}</div>
          ) : null}
        </div>

        <div
          style={{
            marginLeft: "auto",
            display: "flex",
            gap: 12,
            fontSize: 14,
            flexWrap: "wrap",
          }}
        >
          <Link to="/stocks" style={{ textDecoration: "none" }}>
            Stocks
          </Link>
          <Link to="/etfs" style={{ textDecoration: "none" }}>
            ETFs
          </Link>
          <Link to="/feed" style={{ textDecoration: "none" }}>
            Feed
          </Link>
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: 12,
          marginBottom: 16,
        }}
      >
        <StatCard label="Buy Score" value={formatScore(headlineScores.buy)} />
        <StatCard label="Valuation Score" value={formatScore(headlineScores.valuation)} />
        <StatCard label="Growth Score" value={formatScore(headlineScores.growth)} />
        <StatCard label="Profitability Score" value={formatScore(headlineScores.profit)} />
        <StatCard label="Defensiveness Score" value={formatScore(headlineScores.defensiveness)} />
        <StatCard
          label="RSI"
          value={formatPlainNumber(headlineScores.rsi, 1)}
          sublabel="Lower can mean more washed out"
        />
      </div>

      <div
        style={{
          border: "1px solid #eee",
          borderRadius: 14,
          background: "#fff",
          padding: 12,
          marginBottom: 16,
        }}
      >
        <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 6 }}>
          {sym} Revenue & Operating Margin by Period
        </div>
        <div style={{ color: "#6b7280", fontSize: 13, marginBottom: 12 }}>
          Revenue is shown as bars and operating margin as a line. Gross margin is included as a second line for extra context.
        </div>

        {loadingFundamentals ? (
          <div style={{ padding: 18, color: "#666" }}>Loading fundamental trend chart…</div>
        ) : fundamentalError ? (
          <div
            style={{
              padding: 16,
              color: "#b91c1c",
              background: "#fef2f2",
              border: "1px solid #fecaca",
              borderRadius: 10,
            }}
          >
            {fundamentalError}
          </div>
        ) : quarterChartRows.length === 0 ||
          quarterChartRows.every(
            (row) =>
              !isFiniteNumber(row.revenue) &&
              !isFiniteNumber(row.operatingMargin) &&
              !isFiniteNumber(row.grossMargin)
          ) ? (
          <div style={{ padding: 18, color: "#666" }}>
            No quarterly / period revenue and margin data available for this ticker.
          </div>
        ) : (
          <div style={{ width: "100%", height: 380 }}>
            <ResponsiveContainer>
              <ComposedChart
                data={quarterChartRows}
                margin={{ top: 24, right: 24, left: 8, bottom: 8 }}
              >
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="shortLabel" />
                <YAxis
                  yAxisId="left"
                  tickFormatter={(v) => formatCurrencyCompact(v)}
                  width={72}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  tickFormatter={(v) => `${v.toFixed(0)}%`}
                  domain={["auto", "auto"]}
                  width={56}
                />
                <Tooltip content={<FundamentalTooltip />} />
                <Legend />

                <Bar
                  yAxisId="left"
                  dataKey="revenue"
                  name="Revenue"
                  fill="#1e3a8a"
                  radius={[4, 4, 0, 0]}
                >
                  <LabelList
                    dataKey="revenue"
                    position="top"
                    formatter={(v) => (isFiniteNumber(v) ? formatCurrencyCompact(v) : "")}
                    style={{ fontSize: 11, fill: "#111827", fontWeight: 700 }}
                  />
                </Bar>

                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="operatingMargin"
                  name="Operating Margin"
                  stroke="#dc2626"
                  strokeWidth={2.5}
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                >
                  <LabelList
                    dataKey="operatingMargin"
                    position="top"
                    formatter={(v) =>
                      isFiniteNumber(v) ? formatPercentMaybeDecimal(v, 1) : ""
                    }
                    style={{ fontSize: 11, fill: "#dc2626", fontWeight: 700 }}
                  />
                </Line>

                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="grossMargin"
                  name="Gross Margin"
                  stroke="#7c3aed"
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                >
                  <LabelList
                    dataKey="grossMargin"
                    position="top"
                    formatter={(v) =>
                      isFiniteNumber(v) ? formatPercentMaybeDecimal(v, 1) : ""
                    }
                    style={{ fontSize: 11, fill: "#7c3aed", fontWeight: 700 }}
                  />
                </Line>
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(320px, 1fr) minmax(420px, 1fr)",
          gap: 16,
          marginBottom: 16,
          alignItems: "start",
        }}
      >
        <div
          style={{
            border: "1px solid #eee",
            borderRadius: 14,
            background: "#fff",
            padding: 12,
          }}
        >
          <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 6 }}>
            {sym} Fundamental Quality
          </div>
          <div style={{ color: "#6b7280", fontSize: 13, marginBottom: 12 }}>
            TAM, moat, and trend metrics from your fundamental trends dataset.
          </div>

          {loadingFundamentals ? (
            <div style={{ padding: 18, color: "#666" }}>Loading quality metrics…</div>
          ) : !trendMetrics ? (
            <div style={{ padding: 18, color: "#666" }}>
              No fundamental quality metrics available.
            </div>
          ) : (
            <>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                  gap: 12,
                  marginBottom: 14,
                }}
              >
                <StatCard label="TAM Score" value={formatScore(trendMetrics.tamScore)} />
                <StatCard label="Moat Score" value={formatScore(trendMetrics.moatScore)} />
                <StatCard
                  label="Op Margin Trend"
                  value={formatScore(trendMetrics.opTrendScore)}
                />
                <StatCard
                  label="Gross Margin Trend"
                  value={formatScore(trendMetrics.grossTrendScore)}
                />
              </div>

              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", minWidth: 620, borderCollapse: "collapse" }}>
                  <thead style={{ background: "#f9fafb" }}>
                    <tr>
                      <th style={tableThStyle}>Metric</th>
                      <th style={tableThStyle}>Value</th>
                      <th style={tableThStyle}>Metric</th>
                      <th style={tableThStyle}>Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={tableTdStyle}>Revenue Growth R01</td>
                      <td
                        style={{
                          ...tableTdStyle,
                          ...getTrendDirectionStyle(trendMetrics.r01),
                        }}
                      >
                        {formatPercentMaybeDecimal(trendMetrics.r01, 2)}
                      </td>
                      <td style={tableTdStyle}>Revenue Growth R12</td>
                      <td
                        style={{
                          ...tableTdStyle,
                          ...getTrendDirectionStyle(trendMetrics.r12),
                        }}
                      >
                        {formatPercentMaybeDecimal(trendMetrics.r12, 2)}
                      </td>
                    </tr>
                    <tr>
                      <td style={tableTdStyle}>Revenue Growth R03</td>
                      <td
                        style={{
                          ...tableTdStyle,
                          ...getTrendDirectionStyle(trendMetrics.r03),
                        }}
                      >
                        {formatPercentMaybeDecimal(trendMetrics.r03, 2)}
                      </td>
                      <td style={tableTdStyle}>Operating Margin Change O03</td>
                      <td
                        style={{
                          ...tableTdStyle,
                          ...getTrendDirectionStyle(trendMetrics.o03),
                        }}
                      >
                        {formatPercentMaybeDecimal(trendMetrics.o03, 2)}
                      </td>
                    </tr>
                    <tr>
                      <td style={tableTdStyle}>Operating Margin Change O01</td>
                      <td
                        style={{
                          ...tableTdStyle,
                          ...getTrendDirectionStyle(trendMetrics.o01),
                        }}
                      >
                        {formatPercentMaybeDecimal(trendMetrics.o01, 2)}
                      </td>
                      <td style={tableTdStyle}>Operating Margin Change O12</td>
                      <td
                        style={{
                          ...tableTdStyle,
                          ...getTrendDirectionStyle(trendMetrics.o12),
                        }}
                      >
                        {formatPercentMaybeDecimal(trendMetrics.o12, 2)}
                      </td>
                    </tr>
                    <tr>
                      <td style={tableTdStyle}>Gross Margin Change G01</td>
                      <td
                        style={{
                          ...tableTdStyle,
                          ...getTrendDirectionStyle(trendMetrics.g01),
                        }}
                      >
                        {formatPercentMaybeDecimal(trendMetrics.g01, 2)}
                      </td>
                      <td style={tableTdStyle}>Gross Margin Change G12</td>
                      <td
                        style={{
                          ...tableTdStyle,
                          ...getTrendDirectionStyle(trendMetrics.g12),
                        }}
                      >
                        {formatPercentMaybeDecimal(trendMetrics.g12, 2)}
                      </td>
                    </tr>
                    <tr>
                      <td style={tableTdStyle}>Gross Margin Change G03</td>
                      <td
                        style={{
                          ...tableTdStyle,
                          ...getTrendDirectionStyle(trendMetrics.g03),
                        }}
                      >
                        {formatPercentMaybeDecimal(trendMetrics.g03, 2)}
                      </td>
                      <td style={tableTdStyle}>Latest Period</td>
                      <td style={tableTdStyle}>
                        {trendMetrics.latestDate ? fmtQuarterDate(trendMetrics.latestDate) : "—"}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        <div
          style={{
            border: "1px solid #eee",
            borderRadius: 14,
            background: "#fff",
            padding: 12,
          }}
        >
          <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 6 }}>
            {sym} Balance Sheet & Dilution
          </div>
          <div style={{ color: "#6b7280", fontSize: 13, marginBottom: 12 }}>
            Separate balance-sheet strength and shareholder dilution metrics from your GitHub dataset.
          </div>

          {loadingBalance ? (
            <div style={{ padding: 18, color: "#666" }}>
              Loading balance sheet / dilution metrics…
            </div>
          ) : balanceError ? (
            <div
              style={{
                padding: 16,
                color: "#b91c1c",
                background: "#fef2f2",
                border: "1px solid #fecaca",
                borderRadius: 10,
              }}
            >
              {balanceError}
            </div>
          ) : !balanceRow ? (
            <div style={{ padding: 18, color: "#666" }}>
              No balance sheet / dilution metrics available for this ticker.
            </div>
          ) : (
            <>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
                  gap: 12,
                  marginBottom: 14,
                }}
              >
                <StatCard
                  label="Balance Sheet Score"
                  value={formatScore(balanceRow.balanceSheetRiskScore)}
                  sublabel="Higher is better"
                  pillStyle={scorePillStyle(
                    parseMaybeNumber(balanceRow.balanceSheetRiskScore),
                    false
                  )}
                />
                <StatCard
                  label="Dilution Risk Score"
                  value={formatScore(balanceRow.dilutionRiskScore)}
                  sublabel="Lower is better"
                  pillStyle={scorePillStyle(
                    parseMaybeNumber(balanceRow.dilutionRiskScore),
                    true
                  )}
                />
                <StatCard
                  label="Net Cash / Debt"
                  value={formatCurrencyCompact(parseMaybeNumber(balanceRow.netCash))}
                />
                <StatCard
                  label="Current Ratio"
                  value={formatPlainNumber(parseMaybeNumber(balanceRow.currentRatio), 2)}
                />
                <StatCard
                  label="Net Buyback Rate (1Y)"
                  value={formatPercentMaybeDecimal(netBuybackRate1Y, 2)}
                  sublabel="Positive = shrinking share count"
                />
              </div>

              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", minWidth: 760, borderCollapse: "collapse" }}>
                  <thead style={{ background: "#f9fafb" }}>
                    <tr>
                      <th style={tableThStyle}>Metric</th>
                      <th style={tableThStyle}>Value</th>
                      <th style={tableThStyle}>Interpretation</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={tableTdStyle}>Cash</td>
                      <td style={tableTdStyle}>
                        {formatCurrencyCompact(parseMaybeNumber(balanceRow.cash))}
                      </td>
                      <td style={tableTdStyle}>Cash on hand</td>
                    </tr>
                    <tr>
                      <td style={tableTdStyle}>Total Debt</td>
                      <td style={tableTdStyle}>
                        {formatCurrencyCompact(parseMaybeNumber(balanceRow.totalDebt))}
                      </td>
                      <td style={tableTdStyle}>Gross debt load</td>
                    </tr>
                    <tr>
                      <td style={tableTdStyle}>Free Cash Flow</td>
                      <td style={tableTdStyle}>
                        {formatCurrencyCompact(parseMaybeNumber(balanceRow.freeCashFlow))}
                      </td>
                      <td style={tableTdStyle}>Internal funding capacity</td>
                    </tr>
                    <tr>
                      <td style={tableTdStyle}>Quick Ratio</td>
                      <td style={tableTdStyle}>
                        {formatPlainNumber(parseMaybeNumber(balanceRow.quickRatio), 2)}
                      </td>
                      <td style={tableTdStyle}>Near-term liquidity</td>
                    </tr>
                    <tr>
                      <td style={tableTdStyle}>Debt / Equity</td>
                      <td style={tableTdStyle}>
                        {formatPlainNumber(parseMaybeNumber(balanceRow.debtToEquity), 2)}
                      </td>
                      <td style={tableTdStyle}>Leverage intensity</td>
                    </tr>
                    <tr>
                      <td style={tableTdStyle}>Shares Outstanding</td>
                      <td style={tableTdStyle}>
                        {formatCurrencyCompact(parseMaybeNumber(balanceRow.sharesOutCurrent)).replace(
                          "$",
                          ""
                        )}
                      </td>
                      <td style={tableTdStyle}>Current share count</td>
                    </tr>
                    <tr>
                      <td style={tableTdStyle}>Share Count Growth 1Y</td>
                      <td style={tableTdStyle}>
                        {formatPercentMaybeDecimal(
                          parseMaybeNumber(balanceRow.shareCountGrowth1Y),
                          2
                        )}
                      </td>
                      <td style={tableTdStyle}>Recent dilution trend</td>
                    </tr>
                    <tr>
                      <td style={tableTdStyle}>Net Buyback Rate 1Y</td>
                      <td
                        style={{
                          ...tableTdStyle,
                          ...getTrendDirectionStyle(netBuybackRate1Y),
                        }}
                      >
                        {formatPercentMaybeDecimal(netBuybackRate1Y, 2)}
                      </td>
                      <td style={tableTdStyle}>
                        Positive means the company reduced share count
                      </td>
                    </tr>
                    <tr>
                      <td style={tableTdStyle}>Share Count Growth 3Y</td>
                      <td style={tableTdStyle}>
                        {formatPercentMaybeDecimal(
                          parseMaybeNumber(balanceRow.shareCountGrowth3Y),
                          2
                        )}
                      </td>
                      <td style={tableTdStyle}>Longer-term dilution trend</td>
                    </tr>
                    <tr>
                      <td style={tableTdStyle}>Stock-Based Compensation</td>
                      <td style={tableTdStyle}>
                        {formatCurrencyCompact(parseMaybeNumber(balanceRow.stockBasedComp))}
                      </td>
                      <td style={tableTdStyle}>Equity comp burden</td>
                    </tr>
                    <tr>
                      <td style={tableTdStyle}>SBC / Revenue</td>
                      <td style={tableTdStyle}>
                        {formatPercentMaybeDecimal(
                          parseMaybeNumber(balanceRow.sbcToRevenue),
                          2
                        )}
                      </td>
                      <td style={tableTdStyle}>
                        Dilution pressure relative to scale
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const tableThStyle = {
  textAlign: "left",
  padding: "12px 14px",
  fontSize: 13,
  color: "#374151",
  borderBottom: "1px solid #e5e7eb",
  whiteSpace: "nowrap",
  verticalAlign: "top",
};

const tableTdStyle = {
  padding: "12px 14px",
  borderBottom: "1px solid #f3f4f6",
  fontSize: 13,
  whiteSpace: "nowrap",
  verticalAlign: "top",
};